import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ConversaService } from '../../services/conversa.service';
import { MensagemService } from '../../services/mensagem.service';
import { SignalRService } from '../../services/signalr.service';
import { AtendenteService } from '../../services/atendente.service';
import { Atendente, AtualizarStatusResponse } from '../../models/atendente.model';
import { Conversa, Chat, ListarChatsResponse } from '../../models/conversa.model';
import { ConversaTransferidaNotification } from '../../models/mensagem.model';
import { ToastService } from 'src/app/shared/toasts/toast-service';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { ErrorMessagesService } from 'src/app/shared/messages/services/error-messages.service';
import { jwtDecode } from 'jwt-decode';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TransferirConversaModalComponent } from './transferir-conversa-modal/transferir-conversa-modal.component';

interface ChatMessage {
  name: string;
  message: string;
  time: string;
  profile?: string;
  align?: 'left' | 'right';
  direcao?: 'inbound' | 'outbound';
  criadoEm?: Date;
  messageId?: string; // ID da mensagem no WhatsApp para identificar acknowledgments
  ackStatus?: 'pending' | 'delivered' | 'read'; // Status de confirmação da mensagem
  data?: Date; // Data da mensagem para agrupamento
}

interface MensagemComSeparador {
  tipo: 'separador' | 'mensagem';
  data?: string; // Texto do separador (Hoje, Ontem, ou data formatada)
  mensagem?: ChatMessage;
}

@Component({
  selector: 'app-whatsapp-attendance',
  templateUrl: './whatsapp-attendance.component.html',
  styleUrl: './whatsapp-attendance.component.scss'
})
export class WhatsappAttendanceComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly destroy$ = new Subject<void>();

  // Conversas
  conversas: Conversa[] = [];
  conversaSelecionada: Conversa | null = null;
  isLoadingConversas = false;
  
  // Chats (nova estrutura)
  chats: Chat[] = [];
  chatSelecionado: Chat | null = null; // Chat atualmente selecionado (para acessar status e ultimaMensagemDoContatoEm)
  isLoadingChats = false;
  chatsOffset = 0;
  chatsLimit = 20;
  chatsHasMore = true;
  chatsTotal = 0;
  chatsLoadingIds: Set<string> = new Set(); // IDs de chats que estão sendo carregados individualmente
  
  // Mensagens
  chatMessagesData: ChatMessage[] = [];
  conversaIdAtual: string | null = null;
  isLoadingMensagens = false;
  
  // Formulário
  formData!: FormGroup;
  chatSubmit = false;
  
  // Usuário atual
  username: string = '';
  profile: string = '';
  
  // Atendente
  atendenteId: string | null = null;
  nomeAtendente: string | null = null; // Nome completo do atendente atual
  statusAtendente: 'online' | 'ausente' | 'offline' = 'offline';
  statusAtendenteAnterior: 'online' | 'ausente' | 'offline' = 'offline';
  isLoadingStatus = false;
  
  // Filtros
  filtroStatus: string = '';
  buscaConversa: string = '';
  
  // Erros
  error = '';
  erro403 = false;

  // Conversas transferidas recentemente (para destacar visualmente)
  conversasTransferidas: Set<string> = new Set();

  @ViewChild('chatScrollContainer', { static: false }) chatScrollContainer?: ElementRef;

  constructor(
    private fb: FormBuilder,
    private conversaService: ConversaService,
    private mensagemService: MensagemService,
    private signalRService: SignalRService,
    private atendenteService: AtendenteService,
    private toastService: ToastService,
    private authService: AuthService,
    private errorMessagesService: ErrorMessagesService,
    private modalService: NgbModal
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Log das informações do usuário logado
    const currentUser = this.authService.currentUserValue;
    console.log(JSON.stringify(currentUser, null, 2));
    
    this.carregarAtendente();
    // this.carregarConversas(); // Comentado - endpoint não deve ser usado
    this.carregarChats(); // Carregar chats da aba Chat
    this.conectarSignalR();
  }

  ngAfterViewInit(): void {
    // Configurar scroll infinito após a view ser inicializada
    if (this.chatScrollContainer) {
      const scrollElement = this.chatScrollContainer.nativeElement.querySelector('.simplebar-content-wrapper');
      if (scrollElement) {
        scrollElement.addEventListener('scroll', (event: Event) => this.onChatsScroll(event));
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.desconectarSignalR();
  }

  initForm(): void {
    this.formData = this.fb.group({
      message: ['', [Validators.required]],
    });
  }

  get form() {
    return this.formData.controls;
  }

  getTenantId(): string {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.tenantId || '';
  }

  getAtendenteId(): string | undefined {
    return this.atendenteId || undefined;
  }

  getUsuarioId(): string {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.userId?.toString() || '';
  }

  carregarAtendente(): void {
    const usuarioId = this.getUsuarioId();
    if (!usuarioId) {
      return;
    }

    this.atendenteService.obterAtendentePorUsuarioId(usuarioId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (atendente: Atendente) => {
          this.atendenteId = atendente.id;
          this.nomeAtendente = atendente.nomeCompleto || null;
          this.statusAtendente = atendente.status;
          this.statusAtendenteAnterior = atendente.status;
        },
        error: (error) => {
          // Se não encontrar atendente (404), não é um erro crítico
          // O atendente pode ser criado posteriormente
          if (error.status !== 404) {
            console.error('Erro ao carregar atendente:', error);
          }
        }
      });
  }

  criarAtendenteSeNaoExiste(usuarioId: string, statusInicial: 'online' | 'ausente' | 'offline'): void {
    this.isLoadingStatus = true;

    // Primeiro, criar o atendente
    this.atendenteService.criarAtendente({ usuarioId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            // Após criar, buscar os dados atualizados do atendente
            this.atendenteService.obterAtendentePorUsuarioId(usuarioId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (atendente: Atendente) => {
                  this.atendenteId = atendente.id;
                  
                  // Se o status inicial for diferente do padrão, atualizar o status
                  if (statusInicial !== atendente.status) {
                    this.atendenteService.atualizarStatus(atendente.id, statusInicial)
                      .pipe(takeUntil(this.destroy$))
                      .subscribe({
                        next: () => {
                          this.statusAtendente = statusInicial;
                          this.statusAtendenteAnterior = statusInicial;
                          this.toastService.show(`Atendente criado com status ${this.getStatusAtendenteLabel(statusInicial)}`, {
                            classname: 'bg-success text-light',
                          });
                          this.isLoadingStatus = false;
                        },
                        error: (error) => {
                          console.error('Erro ao atualizar status após criar atendente:', error);
                          this.statusAtendente = atendente.status;
                          this.statusAtendenteAnterior = atendente.status;
                          this.toastService.show(`Atendente criado com status ${this.getStatusAtendenteLabel(atendente.status)}`, {
                            classname: 'bg-success text-light',
                          });
                          this.isLoadingStatus = false;
                        }
                      });
                  } else {
                    this.statusAtendente = atendente.status;
                    this.statusAtendenteAnterior = atendente.status;
                    this.toastService.show(`Atendente criado com status ${this.getStatusAtendenteLabel(atendente.status)}`, {
                      classname: 'bg-success text-light',
                    });
                    this.isLoadingStatus = false;
                  }
                },
                error: (error) => {
                  // Se falhar ao buscar, usar os dados da resposta e tentar atualizar status
                  this.atendenteId = response.data.id;
                  this.atendenteService.atualizarStatus(response.data.id, statusInicial)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                      next: () => {
                        this.statusAtendente = statusInicial;
                        this.statusAtendenteAnterior = statusInicial;
                        this.toastService.show(`Atendente criado com status ${this.getStatusAtendenteLabel(statusInicial)}`, {
                          classname: 'bg-success text-light',
                        });
                        this.isLoadingStatus = false;
                      },
                      error: (updateError) => {
                        console.error('Erro ao atualizar status:', updateError);
                        this.statusAtendente = statusInicial;
                        this.statusAtendenteAnterior = statusInicial;
                        this.toastService.show(`Atendente criado. Erro ao definir status inicial.`, {
                          classname: 'bg-warning text-light',
                        });
                        this.isLoadingStatus = false;
                      }
                    });
                }
              });
          } else {
            this.isLoadingStatus = false;
            this.toastService.show('Erro ao criar atendente', {
              classname: 'bg-danger text-light',
            });
          }
        },
        error: (error) => {
          console.error('Erro ao criar atendente:', error);
          this.isLoadingStatus = false;
          this.toastService.show('Erro ao criar atendente. Tente novamente.', {
            classname: 'bg-danger text-light',
          });
          this.statusAtendente = this.statusAtendenteAnterior;
        }
      });
  }

  atualizarStatusAtendente(novoStatus: 'online' | 'ausente' | 'offline'): void {
    // Salvar o status anterior
    this.statusAtendenteAnterior = this.statusAtendente;

    if (!this.atendenteId) {
      // Se não tem atendenteId, tentar criar o atendente primeiro
      const usuarioId = this.getUsuarioId();
      if (usuarioId) {
        this.criarAtendenteSeNaoExiste(usuarioId, novoStatus);
        return;
      } else {
        this.toastService.show('Usuário não encontrado. Por favor, faça login novamente.', {
          classname: 'bg-warning text-light',
        });
        this.statusAtendente = this.statusAtendenteAnterior;
        return;
      }
    }

    if (this.isLoadingStatus) {
      // Reverter o valor se estiver carregando
      this.statusAtendente = this.statusAtendenteAnterior;
      return;
    }

    // Não atualizar se já está no mesmo status
    if (this.statusAtendente === novoStatus) {
      return;
    }

    this.isLoadingStatus = true;

    this.atendenteService.atualizarStatus(this.atendenteId, novoStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: AtualizarStatusResponse) => {
          // A resposta vem como { atendenteId: string, status: string }
          // Após atualizar, buscar os dados atualizados do atendente
          const usuarioId = this.getUsuarioId();
          if (usuarioId) {
            this.atendenteService.obterAtendentePorUsuarioId(usuarioId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (atendente: Atendente) => {
                  this.atendenteId = atendente.id;
                  this.statusAtendente = atendente.status;
                  this.statusAtendenteAnterior = atendente.status;
                  this.toastService.show(`Status alterado para ${this.getStatusAtendenteLabel(atendente.status)}`, {
                    classname: 'bg-success text-light',
                  });
                  this.isLoadingStatus = false;
                },
                error: (error) => {
                  // Se falhar ao buscar, usar o status da resposta
                  this.statusAtendente = response.status;
                  this.statusAtendenteAnterior = response.status;
                  this.toastService.show(`Status alterado para ${this.getStatusAtendenteLabel(response.status)}`, {
                    classname: 'bg-success text-light',
                  });
                  this.isLoadingStatus = false;
                }
              });
          } else {
            // Fallback se não tiver usuarioId
            this.statusAtendente = response.status;
            this.statusAtendenteAnterior = response.status;
            this.toastService.show(`Status alterado para ${this.getStatusAtendenteLabel(response.status)}`, {
              classname: 'bg-success text-light',
            });
            this.isLoadingStatus = false;
          }
        },
        error: (error) => {
          console.error('Erro ao atualizar status:', error);
          this.toastService.show('Erro ao atualizar status do atendente', {
            classname: 'bg-danger text-light',
          });
          // Reverter o valor em caso de erro
          this.statusAtendente = this.statusAtendenteAnterior;
          this.isLoadingStatus = false;
        }
      });
  }

  onStatusChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const novoStatus = selectElement.value as 'online' | 'ausente' | 'offline';
    this.atualizarStatusAtendente(novoStatus);
  }

  getStatusAtendenteLabel(status: 'online' | 'ausente' | 'offline'): string {
    switch (status) {
      case 'online':
        return 'Online';
      case 'ausente':
        return 'Ausente';
      case 'offline':
        return 'Offline';
      default:
        return status;
    }
  }

  getStatusIconClass(status: 'online' | 'ausente' | 'offline'): string {
    switch (status) {
      case 'online':
        return 'mdi mdi-circle text-success';
      case 'ausente':
        return 'mdi mdi-circle text-warning';
      case 'offline':
        return 'mdi mdi-circle text-secondary';
      default:
        return 'mdi mdi-circle text-secondary';
    }
  }

  async conectarSignalR(): Promise<void> {
    const tenantId = this.getTenantId();
    const atendenteId = this.getAtendenteId();
    
    if (tenantId) {
      await this.signalRService.startConnection(tenantId, atendenteId);
      
      // Escutar novas mensagens
      this.signalRService.novaMensagem$
        .pipe(takeUntil(this.destroy$))
        .subscribe(notification => {
          if (notification) {
            this.handleNovaMensagem(notification);
          }
        });
      
      // Escutar mensagens enviadas confirmadas
      this.signalRService.mensagemEnviada$
        .pipe(takeUntil(this.destroy$))
        .subscribe(data => {
          if (data) {
            this.processarMensagemEnviada(data);
          }
        });
      
      // Escutar acknowledgments de mensagens (confirmações de recebimento/leitura)
      this.signalRService.messageAck$
        .pipe(takeUntil(this.destroy$))
        .subscribe(ackData => {
          if (ackData) {
            this.processarMessageAck(ackData);
          }
        });

      // Escutar notificações de conversa transferida
      this.signalRService.conversaTransferida$
        .pipe(takeUntil(this.destroy$))
        .subscribe(notification => {
          if (notification) {
            this.handleConversaTransferida(notification);
          }
        });
    }
  }

  async desconectarSignalR(): Promise<void> {
    const tenantId = this.getTenantId();
    const atendenteId = this.getAtendenteId();
    
    if (tenantId) {
      await this.signalRService.stopConnection(tenantId, atendenteId);
    }
  }

  handleNovaMensagem(notification: any): void {
    // Atualizar lista de chats na aba CHAT
    this.atualizarListaChats(notification);
    
    // Atualizar lista de conversas na aba de atendimento
    this.atualizarListaConversas(notification);
    
    // Se a mensagem é da conversa atual, adicionar à lista de mensagens
    if (notification.conversaId === this.conversaIdAtual) {
      this.processarNovaMensagem(notification);
    }
  }

  /**
   * Processa uma nova mensagem recebida via SignalR
   */
  processarNovaMensagem(data: any): void {
    if (data.conversaId !== this.conversaIdAtual) {
      console.log('Mensagem não é da conversa atual. Ignorando.');
      return;
    }

    // Converter timestamp para Date
    const timestamp = data.timestamp instanceof Date 
      ? data.timestamp 
      : new Date(data.timestamp);

    const novaMensagem: ChatMessage = {
      name: this.conversaSelecionada?.nomeContato || data.numeroRemetente || 'Contato',
      message: data.mensagem || '',
      time: this.formatarHora(timestamp),
      align: 'left',
      direcao: 'inbound',
      criadoEm: timestamp,
      data: timestamp
    };

    // Verificar se a mensagem já não existe (evitar duplicatas)
    const mensagemJaExiste = this.chatMessagesData.some(
      msg => msg.criadoEm?.getTime() === timestamp.getTime() && 
             msg.message === novaMensagem.message
    );

    if (!mensagemJaExiste) {
      this.chatMessagesData.push(novaMensagem);
      
      // Ordenar por data (caso a mensagem tenha chegado fora de ordem)
      this.chatMessagesData.sort((a, b) => 
        (a.criadoEm?.getTime() || 0) - (b.criadoEm?.getTime() || 0)
      );
      
      // Scroll para última mensagem
      setTimeout(() => this.scrollToBottom(), 50);
      
      console.log('Nova mensagem adicionada à conversa:', novaMensagem);
    } else {
      console.log('Mensagem já existe na lista. Ignorando duplicata.');
    }
  }

  /**
   * Processa uma mensagem enviada confirmada via SignalR
   */
  processarMensagemEnviada(data: any): void {
    if (data.conversaId !== this.conversaIdAtual) {
      console.log('Mensagem enviada não é da conversa atual. Ignorando.');
      return;
    }

    // Converter timestamp para Date
    const timestamp = data.timestamp instanceof Date 
      ? data.timestamp 
      : new Date(data.timestamp || new Date());

    const novaMensagem: ChatMessage = {
      name: this.nomeAtendente || this.getUsuarioNome() || 'Você',
      message: data.mensagem || '',
      time: this.formatarHora(timestamp),
      align: 'right',
      direcao: 'outbound',
      criadoEm: timestamp,
      data: timestamp,
      messageId: data.providerMessageId || data.mensagemId || undefined, // ID da mensagem no WhatsApp
      ackStatus: 'pending' // Status inicial: enviada (uma marca cinza)
    };

    // Verificar se a mensagem já não existe (evitar duplicatas)
    const mensagemJaExiste = this.chatMessagesData.some(
      msg => msg.criadoEm?.getTime() === timestamp.getTime() && 
             msg.message === novaMensagem.message &&
             msg.direcao === 'outbound'
    );

    if (!mensagemJaExiste) {
      this.chatMessagesData.push(novaMensagem);
      
      // Ordenar por data
      this.chatMessagesData.sort((a, b) => 
        (a.criadoEm?.getTime() || 0) - (b.criadoEm?.getTime() || 0)
      );
      
      // Scroll para última mensagem
      setTimeout(() => this.scrollToBottom(), 50);
      
      console.log('Mensagem enviada confirmada e adicionada:', novaMensagem);
    } else {
      console.log('Mensagem enviada já existe na lista. Ignorando duplicata.');
    }
  }

  /**
   * Processa acknowledgment de mensagem (confirmação de recebimento/leitura)
   */
  handleConversaTransferida(notification: ConversaTransferidaNotification): void {
    console.log('Conversa transferida recebida:', notification);
    
    // Verificar se o atendente atual é o destino da transferência
    const atendenteAtualId = this.atendenteId;
    const ehAtendenteDestino = atendenteAtualId && notification.atendenteDestinoId && 
                                atendenteAtualId === notification.atendenteDestinoId;
    
    // Verificar se a conversa transferida é a conversa atual
    const conversaIdAtual = this.conversaSelecionada?.id || this.chatSelecionado?.conversaId;
    const ehConversaAtual = notification.conversaId === conversaIdAtual;
    
    if (ehConversaAtual) {
      // Atualizar informações da conversa atual temporariamente
      if (this.chatSelecionado) {
        this.chatSelecionado.atendenteId = notification.atendenteDestinoId;
        this.chatSelecionado.nomeAtendente = notification.atendenteDestinoNome;
        if (ehAtendenteDestino) {
          this.chatSelecionado.foiTransferida = true;
          this.chatSelecionado.transferidaEm = new Date();
        }
      }
      if (this.conversaSelecionada) {
        this.conversaSelecionada.atendenteId = notification.atendenteDestinoId;
        this.conversaSelecionada.nomeAtendente = notification.atendenteDestinoNome;
      }
    }
    
    // Se o atendente atual é o destino, marcar a conversa como transferida
    if (ehAtendenteDestino) {
      this.conversasTransferidas.add(notification.conversaId);
      
      // Remover a flag após 5 minutos (para não ficar destacado para sempre)
      setTimeout(() => {
        this.conversasTransferidas.delete(notification.conversaId);
      }, 5 * 60 * 1000); // 5 minutos
    }
    
    // Recarregar lista de chats do servidor para obter dados atualizados (incluindo usuarioId)
    // Isso garante que o usuarioId seja atualizado corretamente e o card fique cinza
    
    // Resetar offset e recarregar do início
    this.chatsOffset = 0;
    this.chatsHasMore = true;
    this.isLoadingChats = false;
    
    this.conversaService.listarChats(
      this.chatsLimit,
      0,
      false // unreadOnly
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListarChatsResponse) => {
          this.chats = response.chats;
          
          // Marcar conversas transferidas na lista recém carregada
          this.chats.forEach(chat => {
            if (chat.conversaId && this.conversasTransferidas.has(chat.conversaId)) {
              chat.foiTransferida = true;
            }
          });
          
          // Reordenar: conversas do atendente logado primeiro, depois as outras
          this.chats = this.ordenarChatsPorAtendente(this.chats);
          
          this.chatsTotal = response.total;
          this.chatsHasMore = response.hasMore;
          this.chatsOffset = this.chats.length;
          this.isLoadingChats = false;
          
          // Atualizar chatSelecionado se for a conversa atual
          if (ehConversaAtual) {
            const chatAtualizado = this.chats.find(c => c.conversaId === notification.conversaId);
            if (chatAtualizado && this.chatSelecionado) {
              // Atualizar chatSelecionado com os dados atualizados do servidor
              this.chatSelecionado = { ...chatAtualizado };
              if (ehAtendenteDestino) {
                this.chatSelecionado.foiTransferida = true;
                this.chatSelecionado.transferidaEm = new Date();
              }
            }
          }
        },
        error: (error) => {
          console.error('Erro ao recarregar chats após transferência:', error);
          this.isLoadingChats = false;
        }
      });
    
    // Mostrar notificação ao usuário
    if (ehAtendenteDestino) {
      // Notificação específica para o atendente destino
      const mensagem = notification.motivo 
        ? `Você recebeu uma conversa transferida de ${notification.atendenteOrigemNome}. Motivo: ${notification.motivo}`
        : `Você recebeu uma conversa transferida de ${notification.atendenteOrigemNome}`;
      
      this.toastService.show(mensagem, {
        classname: 'bg-success text-light',
      });
    } else {
      // Notificação para outros atendentes (origem ou observadores)
      const mensagem = notification.motivo 
        ? `Conversa transferida para ${notification.atendenteDestinoNome}. Motivo: ${notification.motivo}`
        : `Conversa transferida para ${notification.atendenteDestinoNome}`;
      
      this.toastService.show(mensagem, {
        classname: 'bg-info text-light',
      });
    }
  }

  /**
   * Verifica se uma conversa foi transferida recentemente
   */
  foiTransferida(chat: Chat): boolean {
    if (!chat.conversaId) {
      return false;
    }
    return this.conversasTransferidas.has(chat.conversaId) || (chat.foiTransferida === true);
  }

  processarMessageAck(ackData: any): void {
    try {
      if (!ackData) {
        console.warn('MessageAck: dados vazios. Ignorando.');
        return;
      }

      console.log('Processando MessageAck:', ackData);
      
      if (!ackData.messageId) {
        console.warn('MessageAck sem messageId. Dados recebidos:', ackData);
        return;
      }

      // Validar ackStatus
      const ackStatus = ackData.ackStatus || (ackData.ack === 0 ? 'pending' : ackData.ack === 1 ? 'delivered' : ackData.ack === 2 ? 'read' : 'pending');
      
      if (!['pending', 'delivered', 'read'].includes(ackStatus)) {
        console.warn(`MessageAck: ackStatus inválido "${ackStatus}". Usando "pending" como padrão.`);
      }

      // Encontrar a mensagem na lista pelo messageId
      const mensagemIndex = this.chatMessagesData.findIndex(
        msg => msg.messageId && msg.messageId === ackData.messageId && msg.direcao === 'outbound'
      );

      if (mensagemIndex >= 0) {
        // Atualizar o status da mensagem
        this.chatMessagesData[mensagemIndex].ackStatus = ackStatus;
        console.log(`Status da mensagem ${ackData.messageId} atualizado para: ${ackStatus}`);
      } else {
        console.warn(`Mensagem com messageId ${ackData.messageId} não encontrada na lista atual. Total de mensagens: ${this.chatMessagesData.length}`);
        // Log para debug: mostrar messageIds disponíveis
        const messageIds = this.chatMessagesData
          .filter(msg => msg.direcao === 'outbound')
          .map(msg => msg.messageId)
          .filter(id => id);
        console.log('MessageIds disponíveis nas mensagens outbound:', messageIds);
      }
    } catch (error) {
      console.error('Erro ao processar MessageAck:', error, ackData);
    }
  }

  /**
   * Atualiza a lista de conversas quando recebe uma nova mensagem
   */
  atualizarListaConversas(notificationData: any): void {
    const conversaId = notificationData.conversaId;
    
    if (!conversaId) {
      return;
    }
    
    // Encontrar a conversa na lista atual
    const conversaIndex = this.conversas.findIndex(c => c.id === conversaId);
    
    if (conversaIndex >= 0) {
      // Conversa já existe - atualizar
      const conversa = this.conversas[conversaIndex];
      
      // Atualizar preview da última mensagem
      conversa.ultimaMensagemPreview = notificationData.mensagem || '';
      
      // Atualizar timestamp da última mensagem
      const timestamp = notificationData.timestamp instanceof Date 
        ? notificationData.timestamp 
        : new Date(notificationData.timestamp);
      conversa.ultimaMensagemEm = timestamp;
      
      // Como "NewMessage" é uma notificação de nova mensagem recebida, 
      // assumimos que é uma mensagem inbound (do contato)
      conversa.ultimaMensagemDoContatoEm = timestamp;
      
      // Incrementar total de mensagens
      conversa.totalMensagens = (conversa.totalMensagens || 0) + 1;
      
      // Mover para o topo (última mensagem recebida)
      this.conversas.splice(conversaIndex, 1);
      this.conversas.unshift(conversa);
    }
  }

  /**
   * Atualiza a lista de chats quando recebe uma nova mensagem
   */
  atualizarListaChats(notificationData: any): void {
    // O conversaId da notificação é o UUID da conversa, não o número do telefone
    const conversaId = notificationData.conversaId;
    
    // Encontrar o chat na lista atual usando conversaId (UUID), não o id (número do telefone)
    const chatIndex = this.chats.findIndex(c => c.conversaId === conversaId);
    
    if (chatIndex >= 0) {
      // Chat já existe - atualizar
      const chat = this.chats[chatIndex];
      
      // Incrementar contador de não lidas se não for a conversa atual
      if (conversaId !== this.conversaIdAtual) {
        chat.unreadCount = (chat.unreadCount || 0) + 1;
      }
      
      // Atualizar última mensagem
      chat.lastMessage = {
        body: notificationData.mensagem || '',
        timestamp: notificationData.timestamp ? new Date(notificationData.timestamp).toISOString() : new Date().toISOString()
      };
      
      // Atualizar ultimaMensagemDoContatoEm (nova mensagem recebida)
      chat.ultimaMensagemDoContatoEm = notificationData.timestamp 
        ? new Date(notificationData.timestamp).toISOString() 
        : new Date().toISOString();
      
      // Atualizar chatSelecionado se for o mesmo chat
      if (this.chatSelecionado && this.chatSelecionado.conversaId === conversaId) {
        this.chatSelecionado = { ...chat };
      }
      
      // Mover para o topo (última mensagem recebida)
      this.chats.splice(chatIndex, 1);
      this.chats.unshift(chat);
      
      // Reordenar: conversas do atendente primeiro, depois as outras
      this.chats = this.ordenarChatsPorAtendente(this.chats);
    } else {
      // Chat novo - não está na lista atual
      
      // Adicionar o chat na lista com os dados disponíveis da notificação
      // Isso evita recarregar toda a lista e mostra o chat imediatamente
      // O id do chat é o número do telefone (numeroRemetente), e conversaId é o UUID
      const novoChat: Chat = {
        id: notificationData.numeroRemetente || conversaId, // id é o número do telefone
        conversaId: conversaId, // conversaId é o UUID da conversa
        name: notificationData.numeroRemetente || 'Contato',
        unreadCount: conversaId !== this.conversaIdAtual ? 1 : 0,
        isGroup: false, // Assumimos que não é grupo, pode ser atualizado depois
        lastMessage: {
          body: notificationData.mensagem || '',
          timestamp: notificationData.timestamp ? new Date(notificationData.timestamp).toISOString() : new Date().toISOString()
        }
      };
      
      // Adicionar no topo da lista
      this.chats.unshift(novoChat);
      
      // Reordenar: conversas do atendente primeiro, depois as outras
      this.chats = this.ordenarChatsPorAtendente(this.chats);
      
      // Marcar como carregando para buscar dados completos em background
      this.chatsLoadingIds.add(conversaId);
      
      // Buscar dados completos do chat em background (sem mostrar loading global)
      this.buscarDadosCompletosDoChat(conversaId);
    }
  }

  /**
   * Busca dados completos de um chat específico em background
   * sem afetar o loading global da lista
   */
  private buscarDadosCompletosDoChat(conversaId: string): void {
    // Recarregar apenas a primeira página para obter dados atualizados
    // Isso vai trazer o chat com dados completos se ele estiver na primeira página
    this.conversaService.listarChats(
      this.chatsLimit,
      0, // offset 0 para pegar a primeira página
      false // unreadOnly
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListarChatsResponse) => {
          // Encontrar o chat atualizado na resposta usando conversaId (UUID)
          const chatAtualizado = response.chats.find(c => c.conversaId === conversaId);
          
          if (chatAtualizado) {
            // Atualizar o chat na lista com os dados completos usando conversaId
            const chatIndex = this.chats.findIndex(c => c.conversaId === conversaId);
            if (chatIndex >= 0) {
              // Preservar o unreadCount apenas se:
              // 1. Já tínhamos incrementado localmente (unreadCountAtual > 0)
              // 2. E não é a conversa atual (não foi aberta ainda)
              const unreadCountAtual = this.chats[chatIndex].unreadCount;
              const unreadCountDaAPI = chatAtualizado.unreadCount || 0;
              
              // Atualizar com dados da API
              this.chats[chatIndex] = { ...chatAtualizado };
              
              // Se a conversa está aberta (é a atual), zerar o contador
              if (conversaId === this.conversaIdAtual) {
                this.chats[chatIndex].unreadCount = 0;
              } else if (unreadCountAtual > unreadCountDaAPI) {
                // Se incrementamos localmente mais do que a API retornou, preservar nosso valor
                // (caso de nova mensagem recebida enquanto carregava dados)
                this.chats[chatIndex].unreadCount = unreadCountAtual;
              }
              // Caso contrário, usar o valor da API (que é a fonte da verdade)
              
              // Atualizar chatSelecionado se for o mesmo chat
              if (this.chatSelecionado && this.chatSelecionado.conversaId === conversaId) {
                this.chatSelecionado = { ...this.chats[chatIndex] };
              }
              
              // Reordenar: conversas do atendente primeiro, depois as outras
              this.chats = this.ordenarChatsPorAtendente(this.chats);
            }
          }
          
          // Remover do set de loading
          this.chatsLoadingIds.delete(conversaId);
        },
        error: (error) => {
          console.error('Erro ao buscar dados completos do chat:', error);
          // Remover do set de loading mesmo em caso de erro
          this.chatsLoadingIds.delete(conversaId);
        }
      });
  }

  /**
   * Verifica se um chat específico está em loading
   */
  isChatLoading(chatId: string): boolean {
    return this.chatsLoadingIds.has(chatId);
  }

  carregarConversas(): void {
    this.isLoadingConversas = true;
    this.error = '';
    
    const filtros: any = {};
    if (this.filtroStatus) {
      filtros.status = this.filtroStatus;
    }
    
    this.conversaService.listarConversas(filtros)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.conversas = response.data.conversas;
          }
          this.isLoadingConversas = false;
        },
        error: (error) => {
          if (error.status === 403) {
            this.erro403 = true;
            this.isLoadingConversas = false;
            return;
          }
          this.error = this.errorMessagesService.getMessage('ERR_0008');
          this.isLoadingConversas = false;
          console.error('Erro ao carregar conversas:', error);
        }
      });
  }

  onFiltroStatusChange(): void {
    // this.carregarConversas(); // Comentado - endpoint não deve ser usado
  }

  /**
   * Carregar chats da instância WhatsApp com paginação
   */
  carregarChats(reset: boolean = false): void {
    if (reset) {
      this.chats = [];
      this.chatsOffset = 0;
      this.chatsHasMore = true;
    }

    if (!this.chatsHasMore || this.isLoadingChats) {
      return;
    }

    this.isLoadingChats = true;

    this.conversaService.listarChats(
      this.chatsLimit,
      this.chatsOffset,
      false // unreadOnly
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ListarChatsResponse) => {
          if (reset) {
            this.chats = response.chats;
          } else {
            this.chats = [...this.chats, ...response.chats];
          }
          
          // Marcar conversas transferidas na lista recém carregada
          this.chats.forEach(chat => {
            if (chat.conversaId && this.conversasTransferidas.has(chat.conversaId)) {
              chat.foiTransferida = true;
            }
          });
          
          // Reordenar: conversas do atendente logado primeiro, depois as outras
          this.chats = this.ordenarChatsPorAtendente(this.chats);
          
          this.chatsTotal = response.total;
          this.chatsHasMore = response.hasMore;
          this.chatsOffset = this.chats.length;
          this.isLoadingChats = false;
        },
        error: (error) => {
          console.error('Erro ao carregar chats:', error);
          this.isLoadingChats = false;
          this.toastService.show('Erro ao carregar chats', {
            classname: 'bg-danger text-light',
          });
        }
      });
  }

  /**
   * Handler para scroll infinito na lista de chats
   * Usa ViewChild ou HostListener para detectar scroll no ngx-simplebar
   */
  onChatsScroll(event: Event): void {
    const target = event.target as HTMLElement;
    // O ngx-simplebar cria um elemento interno com classe 'simplebar-content-wrapper'
    const scrollableElement = target.closest('.simplebar-content-wrapper') || target;
    
    if (scrollableElement) {
      const scrollTop = scrollableElement.scrollTop;
      const scrollHeight = scrollableElement.scrollHeight;
      const clientHeight = scrollableElement.clientHeight;

      // Carregar mais quando estiver próximo do final (80% do scroll)
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        if (this.chatsHasMore && !this.isLoadingChats) {
          this.carregarChats(false);
        }
      }
    }
  }

  /**
   * Converter Chat para Conversa (para compatibilidade com código existente)
   */
  chatParaConversa(chat: Chat): Conversa {
    return {
      id: chat.id,
      numeroContato: chat.id,
      nomeContato: chat.name,
      status: chat.unreadCount > 0 ? 'aguardando' : 'aberta',
      totalMensagens: chat.unreadCount,
      ultimaMensagemEm: chat.lastMessage?.timestamp ? new Date(chat.lastMessage.timestamp) : undefined,
    };
  }

  /**
   * Selecionar um chat
   */
  selecionarChat(chat: Chat): void {
    console.log('=== Clicou em um Chat ===');
    console.log('Chat completo:', chat);
    console.log('--- Atributos do Chat ---');
    console.log('ID (número do telefone):', chat.id);
    console.log('Conversa ID (UUID):', chat.conversaId);
    console.log('Name:', chat.name);
    console.log('Unread Count:', chat.unreadCount);
    console.log('Is Group:', chat.isGroup);
    console.log('Last Message:', chat.lastMessage ? {
      body: chat.lastMessage.body,
      timestamp: chat.lastMessage.timestamp
    } : 'null');
    console.log('--- Fim dos Atributos do Chat ---');
    
    // Armazenar o chat selecionado para acessar status e ultimaMensagemDoContatoEm
    this.chatSelecionado = chat;
    
    // Zerar contador de mensagens não vistas ao selecionar o chat
    if (chat.conversaId) {
      this.zerarContadorMensagensNaoVistas(chat.conversaId);
    }
    
    // Verificar se o chat tem conversaId
    if (!chat.conversaId || chat.conversaId === null) {
      console.log('⚠️ ConversaId é null. Não há mensagens na base de dados para este contato.');
      
      // Criar uma conversa temporária apenas para exibir informações
      const conversaTemporaria: Conversa = {
        id: '', // Sem ID pois não existe no banco
        numeroContato: chat.id,
        nomeContato: chat.name,
        status: 'aberta',
        totalMensagens: 0,
        ultimaMensagemEm: chat.lastMessage?.timestamp ? new Date(chat.lastMessage.timestamp) : undefined,
      };
      
      // Selecionar a conversa mas sem carregar mensagens
      this.conversaSelecionada = conversaTemporaria;
      this.conversaIdAtual = null; // Marcar como null para indicar que não há conversa no banco
      this.username = chat.name || 'Contato';
      this.profile = '';
      
      // Limpar mensagens e mostrar mensagem informativa
      this.chatMessagesData = [];
      this.isLoadingMensagens = false;
      
      console.log('Conversa selecionada sem conversaId. Mensagem informativa será exibida.');
      return;
    }
    
    // Se tem conversaId, proceder normalmente
    console.log('✅ ConversaId encontrado no chat!');
    console.log('Usando UUID da conversa para carregar mensagens:', chat.conversaId);
    
    // Buscar a conversa completa na lista para ter todos os dados
    const conversaExistente = this.conversas.find(c => c.id === chat.conversaId);
    
    if (conversaExistente) {
      console.log('✅ Conversa encontrada na lista! Usando dados completos.');
      this.selecionarConversa(conversaExistente);
    } else {
      console.log('Conversa não encontrada na lista, mas temos o conversaId. Criando conversa temporária.');
      // Criar uma conversa temporária com os dados do chat
      const conversaTemporaria: Conversa = {
        id: chat.conversaId,
        numeroContato: chat.id,
        nomeContato: chat.name,
        status: (chat.status as 'aguardando' | 'aberta' | 'fechada') || (chat.unreadCount > 0 ? 'aguardando' : 'aberta'),
        totalMensagens: chat.unreadCount,
        ultimaMensagemEm: chat.lastMessage?.timestamp ? new Date(chat.lastMessage.timestamp) : undefined,
        ultimaMensagemDoContatoEm: chat.ultimaMensagemDoContatoEm ? new Date(chat.ultimaMensagemDoContatoEm) : undefined,
      };
      this.selecionarConversa(conversaTemporaria);
    }
  }

  /**
   * Busca uma conversa pelo número do contato
   */
  private buscarConversaPorNumero(chat: Chat): void {
    // Buscar na lista de conversas com filtro pelo número
    this.isLoadingMensagens = true;
    
    this.conversaService.listarConversas({
      // Não temos filtro direto por número, então vamos buscar todas e filtrar
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            // Procurar conversa pelo número do contato
            const conversaEncontrada = response.data.conversas.find(c => {
              const numeroChat = chat.id.replace(/[^0-9]/g, '');
              const numeroConversa = c.numeroContato?.replace(/[^0-9]/g, '') || '';
              return numeroConversa === numeroChat || c.numeroContato === chat.id;
            });
            
            if (conversaEncontrada) {
              console.log('Conversa encontrada:', conversaEncontrada);
              // Atualizar lista de conversas
              this.conversas = response.data.conversas;
              this.selecionarConversa(conversaEncontrada);
            } else {
              console.warn('Conversa não encontrada para o número:', chat.id);
              // Criar uma conversa temporária com o ID do chat
              // Mas isso não vai funcionar para carregar mensagens
              // Vamos tentar usar o número diretamente como conversaId
              const conversaTemporaria = this.chatParaConversa(chat);
              this.selecionarConversa(conversaTemporaria);
              
              this.toastService.show('Conversa não encontrada. Tentando carregar mensagens...', {
                classname: 'bg-warning text-light',
              });
            }
          }
          this.isLoadingMensagens = false;
        },
        error: (error) => {
          console.error('Erro ao buscar conversa:', error);
          this.isLoadingMensagens = false;
          // Em caso de erro, tentar usar o número diretamente
          const conversaTemporaria = this.chatParaConversa(chat);
          this.selecionarConversa(conversaTemporaria);
        }
      });
  }

  selecionarConversa(conversa: Conversa): void {
    console.log('=== Selecionando Conversa ===');
    console.log('Conversa completa:', conversa);
    console.log('--- Atributos da Conversa ---');
    console.log('ID (UUID):', conversa.id);
    console.log('Atendente ID:', conversa.atendenteId || 'null');
    console.log('Nome do Atendente:', conversa.nomeAtendente || 'null');
    console.log('Número do Contato:', conversa.numeroContato || 'null');
    console.log('Nome do Contato:', conversa.nomeContato || 'null');
    console.log('Status:', conversa.status);
    console.log('Total de Mensagens:', conversa.totalMensagens);
    console.log('Última Mensagem Em:', conversa.ultimaMensagemEm ? new Date(conversa.ultimaMensagemEm).toISOString() : 'null');
    console.log('Última Mensagem do Contato Em:', conversa.ultimaMensagemDoContatoEm ? new Date(conversa.ultimaMensagemDoContatoEm).toISOString() : 'null');
    console.log('Criada Em:', conversa.criadaEm ? new Date(conversa.criadaEm).toISOString() : 'null');
    console.log('--- Fim dos Atributos ---');
    
    this.conversaSelecionada = conversa;
    // IMPORTANTE: Usar o ID (UUID) da conversa, não o número do telefone
    this.conversaIdAtual = conversa.id || null;
    this.username = conversa.nomeContato || conversa.numeroContato || 'Contato';
    this.profile = '';
    
    // Atualizar chatSelecionado se houver um chat correspondente na lista
    if (conversa.id) {
      const chatCorrespondente = this.chats.find(c => c.conversaId === conversa.id);
      if (chatCorrespondente) {
        this.chatSelecionado = { ...chatCorrespondente };
        // Zerar contador de mensagens não vistas ao abrir a conversa
        this.zerarContadorMensagensNaoVistas(conversa.id);
      }
    }
    
    console.log('Conversa selecionada. ID atual:', this.conversaIdAtual);
    console.log('Username:', this.username);
    
    // Só carregar mensagens se tiver conversaId válido
    if (conversa.id) {
      // Carregar mensagens usando o UUID da conversa
      console.log('Carregando mensagens com UUID:', conversa.id);
      this.carregarMensagens(conversa.id);
    } else {
      console.log('⚠️ Conversa sem ID. Não carregando mensagens do banco.');
      // Limpar mensagens e mostrar mensagem informativa
      this.chatMessagesData = [];
      this.isLoadingMensagens = false;
    }
    
    console.log('=== Fim da Seleção de Conversa ===');
  }

  carregarMensagens(conversaId: string): void {
    console.log('Carregando mensagens da conversa:', conversaId);
    this.isLoadingMensagens = true;
    this.chatMessagesData = [];
    
    this.mensagemService.listarMensagensPorConversa(conversaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('Mensagens carregadas:', response.mensagens?.length || 0);
          
          // Converter mensagens para o formato ChatMessage
          this.chatMessagesData = response.mensagens.map(msg => {
            // Converter timestamp string para Date se necessário
            const criadoEm = msg.criadoEm instanceof Date 
              ? msg.criadoEm 
              : new Date(msg.criadoEm || msg.timestamp);
            
            return {
              name: msg.direcao === 'outbound' 
                ? (this.nomeAtendente || this.getUsuarioNome() || 'Você')
                : (this.conversaSelecionada?.nomeContato || msg.numeroRemetente),
              message: msg.mensagem,
              time: this.formatarHora(criadoEm),
              align: msg.direcao === 'outbound' ? 'right' : 'left',
              direcao: msg.direcao,
              criadoEm: criadoEm,
              data: criadoEm,
              messageId: (msg as any).messageId || (msg as any).providerMessageId || undefined, // ID da mensagem no WhatsApp
              ackStatus: (msg as any).ackStatus || (msg.direcao === 'outbound' ? 'pending' : undefined) // Status inicial para mensagens enviadas
            };
          });
          
          // Ordenar por data (mais antigas primeiro)
          this.chatMessagesData.sort((a, b) => 
            a.criadoEm!.getTime() - b.criadoEm!.getTime()
          );
          
          // Marcar mensagens como lidas no backend APENAS se o usuário atual for o dono da conversa
          // Isso atualiza o unreadCount no banco de dados
          if (this.chatSelecionado && this.pertenceAoUsuarioLogado(this.chatSelecionado)) {
            this.mensagemService.marcarMensagensComoLidas(conversaId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  console.log('✅ Mensagens marcadas como lidas no backend');
                  // Zerar contador localmente após marcar como lida no backend
                  this.zerarContadorMensagensNaoVistas(conversaId);
                },
                error: (error) => {
                  console.warn('⚠️ Erro ao marcar mensagens como lidas (não crítico):', error);
                  // Mesmo com erro, zerar contador localmente para melhor UX
                  this.zerarContadorMensagensNaoVistas(conversaId);
                }
              });
          } else {
            console.log('ℹ️ Mensagens não marcadas como lidas: usuário não é o dono da conversa');
            // Mesmo não sendo o dono, zerar contador localmente para melhor UX
            this.zerarContadorMensagensNaoVistas(conversaId);
          }
          
          this.isLoadingMensagens = false;
          // Aguardar um pouco para garantir que o DOM foi atualizado
          setTimeout(() => this.scrollToBottom(), 100);
        },
        error: (error) => {
          console.error('Erro ao carregar mensagens:', error);
          this.isLoadingMensagens = false;
          this.toastService.show('Erro ao carregar mensagens', {
            classname: 'bg-danger text-light',
          });
        }
      });
  }

  /**
   * Verifica se um chat específico está fora da janela de 24 horas
   */
  estaForaDaJanela24Horas(chat: Chat): boolean {
    // Se não tem ultimaMensagemDoContatoEm, está fora da janela
    if (!chat.ultimaMensagemDoContatoEm) {
      return true;
    }

    const dataUltimaMensagem = new Date(chat.ultimaMensagemDoContatoEm);
    const agora = new Date();
    const janela24Horas = new Date(dataUltimaMensagem.getTime() + 24 * 60 * 60 * 1000); // Adiciona 24 horas

    return janela24Horas < agora;
  }

  /**
   * Verifica se pode enviar mensagem baseado nas regras:
   * 1. Status da conversa não pode ser "fechada" (deve ser "aguardando" ou "aberta")
   * 2. Janela de 24 horas: ultimaMensagemDoContatoEm deve existir e ultimaMensagemDoContatoEm + 24 horas >= agora
   */
  podeEnviarMensagem(): boolean {
    if (!this.chatSelecionado) {
      return false;
    }

    // Regra 1: Verificar se a conversa pertence ao usuário logado
    if (!this.pertenceAoUsuarioLogado(this.chatSelecionado)) {
      return false;
    }

    // Regra 2: Verificar status da conversa
    const status = this.chatSelecionado.status;
    if (status === 'fechada' || status === null || status === undefined) {
      return false;
    }

    // Regra 3: Verificar janela de 24 horas
    if (this.estaForaDaJanela24Horas(this.chatSelecionado)) {
      return false;
    }

    return true;
  }

  messageSave(): void {
    this.chatSubmit = true;
    
    if (this.formData.invalid || !this.conversaIdAtual) {
      this.chatSubmit = false;
      return;
    }

    // Validação de segurança: verificar se pode enviar mensagem (impede bypass via devtools)
    if (!this.podeEnviarMensagem()) {
      this.toastService.show('Não é possível enviar mensagem. A conversa está fechada ou a janela de 24 horas expirou.', {
        classname: 'bg-warning text-light',
      });
      this.chatSubmit = false;
      return;
    }

    const message = this.formData.get('message')?.value?.trim();
    if (!message) {
      this.chatSubmit = false;
      return;
    }

    // Validar tamanho máximo (4096 caracteres)
    if (message.length > 4096) {
      this.toastService.show('Mensagem muito longa. Máximo de 4096 caracteres.', {
        classname: 'bg-warning text-light',
      });
      this.chatSubmit = false;
      return;
    }

    console.log('Enviando mensagem:', {
      conversaId: this.conversaIdAtual,
      mensagem: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
      tamanho: message.length
    });

    this.mensagemService.enviarMensagem(this.conversaIdAtual, message, 'text')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ Mensagem enviada com sucesso:', response);
          console.log('Mensagem ID:', response.mensagemId);
          console.log('Provider Message ID:', response.providerMessageId);
          
          // Limpar formulário imediatamente após sucesso
          this.formData.reset();
          this.chatSubmit = false;
          
          // NÃO adicionar mensagem manualmente aqui!
          // O SignalR vai notificar automaticamente via "MessageSent"
          // e o método processarMensagemEnviada() vai adicionar à lista
          
          this.toastService.show('Mensagem enviada!', {
            classname: 'bg-success text-light',
          });
        },
        error: (error) => {
          console.error('❌ Erro ao enviar mensagem:', error);
          this.chatSubmit = false;
          
          // O erro já é tratado no serviço com toast
          // Mas podemos adicionar log adicional aqui se necessário
        }
      });
  }

  fecharConversa(): void {
    if (!this.conversaSelecionada) {
      return;
    }

    this.conversaService.fecharConversa(this.conversaSelecionada.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.toastService.show('Conversa fechada com sucesso', {
              classname: 'bg-success text-light',
            });
            this.conversaSelecionada = null;
            this.chatSelecionado = null;
            this.conversaIdAtual = null;
            this.chatMessagesData = [];
            // this.carregarConversas(); // Comentado - endpoint não deve ser usado
          }
        },
        error: (error) => {
          console.error('Erro ao fechar conversa:', error);
        }
      });
  }

  abrirModalTransferirConversa(): void {
    // Verificar se há conversa selecionada
    const conversaId = this.conversaSelecionada?.id || this.chatSelecionado?.conversaId;
    if (!conversaId) {
      this.toastService.show('Nenhuma conversa selecionada', {
        classname: 'bg-warning text-light',
      });
      return;
    }

    // Verificar se a conversa está fechada
    const status = this.conversaSelecionada?.status || this.chatSelecionado?.status;
    if (status === 'fechada') {
      this.toastService.show('Não é possível transferir uma conversa fechada', {
        classname: 'bg-warning text-light',
      });
      return;
    }

    // Abrir modal de transferência
    const modalRef = this.modalService.open(TransferirConversaModalComponent, {
      size: 'md',
      centered: true
    });

    modalRef.componentInstance.conversaId = conversaId;
    modalRef.componentInstance.nomeContato = this.conversaSelecionada?.nomeContato || this.chatSelecionado?.name;

    // Processar resultado da modal
    modalRef.result.then(
      (result) => {
        if (result && result.atendenteId) {
          // Passar o atendenteId como atendenteDestinoId para a API
          this.transferirConversa(conversaId, result.atendenteId, result.motivo);
        }
      },
      () => {
        // Modal foi fechada sem ação (dismiss)
      }
    );
  }

  transferirConversa(conversaId: string, atendenteId: string, motivo?: string): void {
    // O atendenteId é passado como atendenteDestinoId para a API
    this.conversaService.transferirConversa(conversaId, atendenteId, motivo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.toastService.show('Conversa transferida com sucesso', {
            classname: 'bg-success text-light',
          });
          
          // Atualizar a lista de chats para refletir a transferência
          this.carregarChats();
          
          // Opcional: limpar a conversa selecionada após transferência
          // this.conversaSelecionada = null;
          // this.chatSelecionado = null;
          // this.conversaIdAtual = null;
          // this.chatMessagesData = [];
        },
        error: (error) => {
          console.error('Erro ao transferir conversa:', error);
          // O erro já é tratado no serviço com toast
        }
      });
  }

  formatarHora(data: Date): string {
    return `${data.getHours().toString().padStart(2, '0')}:${data.getMinutes().toString().padStart(2, '0')}`;
  }

  /**
   * Verifica se uma data é hoje
   */
  ehHoje(data: Date): boolean {
    const hoje = new Date();
    return data.getDate() === hoje.getDate() &&
           data.getMonth() === hoje.getMonth() &&
           data.getFullYear() === hoje.getFullYear();
  }

  /**
   * Verifica se uma data é ontem
   */
  ehOntem(data: Date): boolean {
    const ontem = new Date();
    ontem.setDate(ontem.getDate() - 1);
    return data.getDate() === ontem.getDate() &&
           data.getMonth() === ontem.getMonth() &&
           data.getFullYear() === ontem.getFullYear();
  }

  /**
   * Formata uma data para exibição (Hoje, Ontem, ou data formatada)
   */
  formatarDataSeparador(data: Date): string {
    if (this.ehHoje(data)) {
      return 'Hoje';
    } else if (this.ehOntem(data)) {
      return 'Ontem';
    } else {
      // Formatar data: "DD/MM/YYYY" ou "DD de Mês de YYYY"
      const dia = data.getDate();
      const mes = data.getMonth();
      const ano = data.getFullYear();
      
      const meses = [
        'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];
      
      return `${dia} de ${meses[mes]} de ${ano}`;
    }
  }

  /**
   * Agrupa mensagens com separadores de data
   */
  getMensagensComSeparadores(): MensagemComSeparador[] {
    if (!this.chatMessagesData || this.chatMessagesData.length === 0) {
      return [];
    }

    const resultado: MensagemComSeparador[] = [];
    let dataAnterior: Date | null = null;

    for (const mensagem of this.chatMessagesData) {
      if (!mensagem.criadoEm) {
        continue;
      }

      const dataMensagem = mensagem.criadoEm;
      
      // Normalizar datas para comparar apenas dia/mês/ano (ignorar hora)
      const dataMensagemNormalizada = new Date(
        dataMensagem.getFullYear(),
        dataMensagem.getMonth(),
        dataMensagem.getDate()
      );
      
      const dataAnteriorNormalizada = dataAnterior 
        ? new Date(
            dataAnterior.getFullYear(),
            dataAnterior.getMonth(),
            dataAnterior.getDate()
          )
        : null;

      // Se a data mudou, adicionar separador
      if (!dataAnteriorNormalizada || 
          dataMensagemNormalizada.getTime() !== dataAnteriorNormalizada.getTime()) {
        resultado.push({
          tipo: 'separador',
          data: this.formatarDataSeparador(dataMensagem)
        });
        dataAnterior = dataMensagem;
      }

      // Adicionar mensagem
      resultado.push({
        tipo: 'mensagem',
        mensagem: mensagem
      });
    }

    return resultado;
  }

  scrollToBottom(): void {
    setTimeout(() => {
      const chatContainer = document.querySelector('.chat-conversation');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 100);
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'aberta':
        return 'bg-success-subtle text-success';
      case 'aguardando':
        return 'bg-warning-subtle text-warning';
      case 'fechada':
        return 'bg-secondary-subtle text-secondary';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'aberta':
        return 'Aberta';
      case 'aguardando':
        return 'Aguardando';
      case 'fechada':
        return 'Fechada';
      default:
        return status;
    }
  }

  getConversasFiltradas(): Conversa[] {
    if (!this.buscaConversa) {
      return this.conversas;
    }
    
    const busca = this.buscaConversa.toLowerCase();
    return this.conversas.filter(conv => 
      (conv.nomeContato?.toLowerCase().includes(busca)) ||
      (conv.numeroContato?.toLowerCase().includes(busca))
    );
  }

  getUsuarioInicial(): string {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.name?.charAt(0)?.toUpperCase() || 'U';
  }

  getUsuarioNome(): string {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.name || 'Usuário';
  }

  formatarHoraConversa(data: Date | string | undefined): string {
    if (!data) return '';
    const date = typeof data === 'string' ? new Date(data) : data;
    return this.formatarHora(date);
  }

  getInicialConversa(conversa: Conversa): string {
    const nome = conversa.nomeContato || conversa.numeroContato || '';
    return nome.charAt(0).toUpperCase() || 'C';
  }

  getInicialConversaSelecionada(): string {
    if (!this.conversaSelecionada) return 'C';
    return this.getInicialConversa(this.conversaSelecionada);
  }

  /**
   * Zera o contador de mensagens não vistas para uma conversa específica
   * Atualiza tanto na lista de chats quanto no chatSelecionado
   */
  zerarContadorMensagensNaoVistas(conversaId: string): void {
    if (!conversaId) {
      return;
    }

    // Encontrar o chat na lista usando conversaId
    const chatIndex = this.chats.findIndex(c => c.conversaId === conversaId);
    
    if (chatIndex >= 0) {
      // Zerar o contador na lista de chats
      this.chats[chatIndex].unreadCount = 0;
      console.log(`✅ Contador de mensagens não vistas zerado para conversa ${conversaId}`);
      
      // Atualizar chatSelecionado se for a mesma conversa
      if (this.chatSelecionado && this.chatSelecionado.conversaId === conversaId) {
        this.chatSelecionado.unreadCount = 0;
      }
    } else {
      console.warn(`⚠️ Chat com conversaId ${conversaId} não encontrado na lista para zerar contador`);
    }
  }

  /**
   * Verifica se a conversa pertence ao usuário logado
   */
  pertenceAoUsuarioLogado(chat: Chat): boolean {
    if (!chat.usuarioId) {
      return false;
    }
    
    const currentUser = this.authService.currentUserValue;
    if (!currentUser || !currentUser.token) {
      return false;
    }

    try {
      // Decodifica o token JWT para obter o UUID do usuário
      const decodedToken: any = jwtDecode(currentUser.token);
      
      // Tenta obter o UUID do usuário do token (pode estar em diferentes campos)
      const usuarioIdFromToken = decodedToken.userId || 
                                  decodedToken.UserId || 
                                  decodedToken.sub || 
                                  decodedToken.nameid;
      
      // Compara o usuarioId do chat com o UUID do token
      if (usuarioIdFromToken) {
        return chat.usuarioId === usuarioIdFromToken.toString();
      }
      
      // Fallback: compara com userId numérico convertido para string
      return chat.usuarioId === currentUser.userId?.toString();
    } catch (error) {
      console.error('Erro ao decodificar token JWT:', error);
      // Fallback: compara com userId numérico convertido para string
      return chat.usuarioId === currentUser.userId?.toString();
    }
  }

  /**
   * Ordena a lista de chats colocando primeiro as conversas do atendente logado,
   * mantendo a ordem global original dentro de cada grupo
   */
  ordenarChatsPorAtendente(chats: Chat[]): Chat[] {
    const chatsDoAtendente: Chat[] = [];
    const outrosChats: Chat[] = [];

    // Separar os chats em dois grupos mantendo a ordem original
    chats.forEach(chat => {
      if (this.pertenceAoUsuarioLogado(chat)) {
        chatsDoAtendente.push(chat);
      } else {
        outrosChats.push(chat);
      }
    });

    // Retornar: primeiro os do atendente, depois os outros
    return [...chatsDoAtendente, ...outrosChats];
  }
}
